/**
 * LiveKit event handlers.
 */
import {
  Room,
  RoomEvent,
  ConnectionState,
  RemoteParticipant,
  RemoteTrackPublication,
  Track,
  LocalTrackPublication,
  VideoQuality,
} from 'livekit-client';
import type { LiveKitCallbacks, LiveKitConnectionState } from './livekitClientTypes';
import { mapRemoteParticipant, mapLocalParticipant } from './livekitClientParticipants';
import { logDebug, logError, logWarning } from '../logging/logger';

const AUDIO_TRACK_BIND_RETRY_MS = 30;
const AUDIO_TRACK_BIND_MAX_ATTEMPTS = 8;
const VIDEO_TRACK_BIND_RETRY_MS = 60;
const VIDEO_TRACK_BIND_MAX_ATTEMPTS = 12;
const ENABLE_SCREEN_SHARE_AUDIO_PLAYBACK = false;

/**
 * Binds LiveKit room event handlers.
 */
export function bindRoomEventHandlers(room: Room, callbacks: LiveKitCallbacks): void {
  room.on(RoomEvent.ParticipantConnected, (participant) => {
    handleParticipantConnected(participant, callbacks);
  });

  room.on(RoomEvent.ParticipantDisconnected, (participant) => {
    handleParticipantDisconnected(participant, callbacks);
  });

  room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
    handleTrackSubscribed(track, publication, participant, callbacks);
  });

  room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
    handleTrackUnsubscribed(track, publication, participant, callbacks);
  });

  room.on(RoomEvent.LocalTrackPublished, (publication) => {
    handleLocalTrackPublished(room, publication, callbacks);
  });

  room.on(RoomEvent.LocalTrackUnpublished, (publication) => {
    handleLocalTrackUnpublished(room, publication, callbacks);
  });

  room.on(RoomEvent.TrackMuted, (_publication, participant) => {
    handleParticipantUpdatedIfRemote(participant, callbacks);
  });

  room.on(RoomEvent.TrackUnmuted, (_publication, participant) => {
    handleParticipantUpdatedIfRemote(participant, callbacks);
  });

  room.on(RoomEvent.ParticipantMetadataChanged, (_metadata, participant) => {
    handleParticipantUpdatedIfRemote(participant, callbacks);
  });

  room.on(RoomEvent.ParticipantNameChanged, (_name, participant) => {
    handleParticipantUpdatedIfRemote(participant, callbacks);
  });

  room.on(RoomEvent.ConnectionStateChanged, (state) => {
    handleConnectionStateChanged(state, callbacks);
  });

  room.on(RoomEvent.Disconnected, () => {
    callbacks.onConnectionStateChanged?.('disconnected');
  });
}

function handleParticipantConnected(
  participant: RemoteParticipant,
  callbacks: LiveKitCallbacks
): void {
  callbacks.onParticipantJoined?.(mapRemoteParticipant(participant));
}

function handleParticipantDisconnected(
  participant: RemoteParticipant,
  callbacks: LiveKitCallbacks
): void {
  callbacks.onParticipantLeft?.(mapRemoteParticipant(participant));
}

function handleTrackSubscribed(
  track: Track,
  publication: RemoteTrackPublication,
  participant: RemoteParticipant,
  callbacks: LiveKitCallbacks
): void {
  const trackType = resolveTrackType(publication.source, publication.trackName, track.kind);
  if (trackType === 'screenAudio' && !ENABLE_SCREEN_SHARE_AUDIO_PLAYBACK) {
    return;
  }

  requestHighVideoQuality(publication, trackType);
  emitTrackSubscribedWithRetry(track, publication, participant, trackType, callbacks);
}

function requestHighVideoQuality(
  publication: RemoteTrackPublication,
  trackType: 'audio' | 'video' | 'screenVideo' | 'screenAudio' | 'roomCamera'
): void {
  if (trackType === 'audio' || trackType === 'screenAudio') {
    return;
  }

  try {
    publication.setVideoQuality(VideoQuality.HIGH);
  } catch {
    // Ignore quality hint failures (older SDKs / non-video publications).
  }
}

function emitTrackSubscribedWithRetry(
  track: Track,
  publication: RemoteTrackPublication,
  participant: RemoteParticipant,
  trackType: 'audio' | 'video' | 'screenVideo' | 'screenAudio' | 'roomCamera',
  callbacks: LiveKitCallbacks
): void {
  const context = {
    participantId: participant.identity,
    trackType,
    trackSid: publication.trackSid,
    trackName: publication.trackName,
    source: publication.source,
  };

  const bindRetryMs =
    trackType === 'audio' || trackType === 'screenAudio'
      ? AUDIO_TRACK_BIND_RETRY_MS
      : VIDEO_TRACK_BIND_RETRY_MS;
  const maxAttempts =
    trackType === 'audio' || trackType === 'screenAudio'
      ? AUDIO_TRACK_BIND_MAX_ATTEMPTS
      : VIDEO_TRACK_BIND_MAX_ATTEMPTS;

  const emit = (attempt: number): void => {
    const mediaTrack = track.mediaStreamTrack;
    if (mediaTrack) {
      logDebug('LiveKit track subscribed', context);
      callbacks.onTrackStarted?.(participant.identity, mediaTrack, trackType, false);
      callbacks.onParticipantUpdated?.(mapRemoteParticipant(participant));
      return;
    }

    if (attempt === 0) {
      logWarning('LiveKit track subscribed before mediaStreamTrack was ready', context);
    }

    if (attempt >= maxAttempts) {
      logError('LiveKit track subscription did not expose mediaStreamTrack in time', context);
      callbacks.onParticipantUpdated?.(mapRemoteParticipant(participant));
      return;
    }

    setTimeout(() => emit(attempt + 1), bindRetryMs);
  };

  emit(0);
}

function handleTrackUnsubscribed(
  track: Track,
  publication: RemoteTrackPublication,
  participant: RemoteParticipant,
  callbacks: LiveKitCallbacks
): void {
  const trackType = resolveTrackType(publication.source, publication.trackName, track.kind);
  if (trackType === 'screenAudio' && !ENABLE_SCREEN_SHARE_AUDIO_PLAYBACK) {
    return;
  }

  const mediaTrack = track.mediaStreamTrack ?? null;

  callbacks.onTrackStopped?.(
    participant.identity,
    mediaTrack,
    trackType,
    false
  );

  callbacks.onParticipantUpdated?.(mapRemoteParticipant(participant));
}

function handleLocalTrackPublished(
  room: Room,
  publication: LocalTrackPublication,
  callbacks: LiveKitCallbacks
): void {
  const track = publication.track;
  if (!track) {
    return;
  }

  const mediaTrack = track.mediaStreamTrack ?? null;
  if (!mediaTrack) {
    logWarning('LiveKit local track published before mediaStreamTrack was ready', {
      participantId: room.localParticipant.identity,
      source: publication.source,
      trackName: publication.trackName,
    });
    return;
  }

  callbacks.onTrackStarted?.(
    room.localParticipant.identity,
    mediaTrack,
    resolveTrackType(publication.source, publication.trackName, track.kind),
    true
  );

  callbacks.onParticipantUpdated?.(mapLocalParticipant(room.localParticipant));
}

function handleLocalTrackUnpublished(
  room: Room,
  publication: LocalTrackPublication,
  callbacks: LiveKitCallbacks
): void {
  const track = publication.track;

  callbacks.onTrackStopped?.(
    room.localParticipant.identity,
    track?.mediaStreamTrack ?? null,
    resolveTrackType(publication.source, publication.trackName, track?.kind),
    true
  );

  callbacks.onParticipantUpdated?.(mapLocalParticipant(room.localParticipant));
}

function handleParticipantUpdated(
  participant: RemoteParticipant,
  callbacks: LiveKitCallbacks
): void {
  callbacks.onParticipantUpdated?.(mapRemoteParticipant(participant));
}

function handleParticipantUpdatedIfRemote(
  participant: unknown,
  callbacks: LiveKitCallbacks
): void {
  if (!(participant instanceof RemoteParticipant)) {
    return;
  }

  handleParticipantUpdated(participant, callbacks);
}

function handleConnectionStateChanged(
  state: ConnectionState,
  callbacks: LiveKitCallbacks
): void {
  callbacks.onConnectionStateChanged?.(mapConnectionState(state));
}

function mapConnectionState(state: ConnectionState): LiveKitConnectionState {
  switch (state) {
    case ConnectionState.Connected:
      return 'connected';
    case ConnectionState.Connecting:
      return 'connecting';
    case ConnectionState.Reconnecting:
      return 'reconnecting';
    default:
      return 'disconnected';
  }
}

function resolveTrackType(
  source: Track.Source,
  trackName?: string,
  trackKind?: Track.Kind
): 'audio' | 'video' | 'screenVideo' | 'screenAudio' | 'roomCamera' {
  // Check for room camera by track name
  if (trackName === 'room_camera') {
    return 'roomCamera';
  }

  // Also check if the name contains "room_camera" (in case of suffix)
  if (trackName && trackName.includes('room_camera')) {
    return 'roomCamera';
  }

  switch (source) {
    case Track.Source.Microphone:
      return 'audio';
    case Track.Source.ScreenShare:
      return 'screenVideo';
    case Track.Source.ScreenShareAudio:
      return 'screenAudio';
    default:
      if (trackKind === 'audio') {
        return 'audio';
      }

      return 'video';
  }
}
